// WorkflowKindChip — visual label for workflow kind (agentic / etl / mixed).
// Kind is a different axis from status; uses gradient/icon rather than tone-color.
import { Bot, Database } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import type { WorkflowKind } from "@/types/workflow.types";

interface WorkflowKindChipProps {
  kind: WorkflowKind;
  size?: "sm" | "md";
  className?: string;
}

const kindStyles: Record<WorkflowKind, string> = {
  // Saturated purple→fuchsia gradient with white text per spec.
  // Token defined in tokens-light.css / tokens-dark.css.
  agentic: "bg-[var(--agentic-grad)] text-white",
  // Slate gradient with white text for ETL kind.
  etl: "bg-[var(--etl-grad)] text-white",
  // Mixed = split agentic + etl; use agentic gradient with reduced opacity as compromise.
  mixed: "bg-[var(--agentic-grad)] text-white opacity-80",
};

const sizeStyles = {
  sm: "h-[22px] px-1.5 text-[10px] gap-1",
  md: "h-[26px] px-2 text-xs gap-1.5",
};

function MixedIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden
    >
      <rect x="0" y="0" width="6" height="12" fill="currentColor" opacity="0.4" />
      <rect x="6" y="0" width="6" height="12" fill="currentColor" opacity="0.8" />
    </svg>
  );
}

export function WorkflowKindChip({ kind, size = "sm", className }: WorkflowKindChipProps) {
  const t = useTranslations("workflows");
  const label = t(`kind.${kind}` as Parameters<typeof t>[0]);

  const Icon =
    kind === "agentic" ? Bot : kind === "etl" ? Database : null;
  const iconSize = size === "sm" ? "size-3" : "size-3.5";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-semibold select-none",
        kindStyles[kind],
        sizeStyles[size],
        className
      )}
    >
      {kind === "mixed" ? (
        <MixedIcon className={iconSize} />
      ) : Icon ? (
        <Icon className={iconSize} aria-hidden />
      ) : null}
      {label}
    </span>
  );
}

export default WorkflowKindChip;
